import { alpha, Button, ButtonProps, useTheme } from "@mui/material";
import { TakeActionMenu } from "./TakeActionMenu";
import { useId, useState } from "react";
import { TriageBarProps } from "../TriageBar/TriageBar";
import { useColorMode } from "~/providers/color-mode";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import { UnresolvedIcon } from "~/icons";

export interface TakeActionButtonProps {
  type?: "small-table" | "action-cell" | "triage-bar";
  availableRemediations?: TriageBarProps["availableRemediations"];
  conversation?: TriageBarProps["conversation"];
}

export function TakeActionButton({
  type = "small-table",
  availableRemediations,
  conversation,
}: TakeActionButtonProps) {
  const theme = useTheme();
  const { mode } = useColorMode();
  const uniqueId = useId();
  const anchorId = "take-action-button-" + uniqueId;
  const menuId = "take-action-menu-" + uniqueId;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick: ButtonProps["onClick"] = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const buttonVariant: ButtonProps["variant"] =
    type === "triage-bar" ? "contained" : "text";
  const buttonSize: ButtonProps["size"] =
    type === "triage-bar" ? "medium" : "small";
  const buttonStartIcon: ButtonProps["startIcon"] =
    type === "action-cell" ? <UnresolvedIcon /> : undefined;
  const buttonEndIcon: ButtonProps["endIcon"] =
    type === "small-table" ? <ArrowForwardIcon /> : undefined;
  const buttonFullWidth: ButtonProps["fullWidth"] =
    type === "triage-bar" ? true : undefined;
  const buttonSx = (): ButtonProps["sx"] => {
    switch (type) {
      case "triage-bar":
        return {
          maxWidth: {
            xs: "100%",
            sm: theme.spacing(16),
            md: theme.spacing(32),
          },
          minWidth: {
            xs: "100%",
            sm: theme.spacing(16),
            md: theme.spacing(32),
          },
          background: (theme) => theme.palette.primary.gradient,
          borderBottomLeftRadius: open ? 0 : undefined,
          borderBottomRightRadius: open ? 0 : undefined,
          color: (theme) => theme.palette.common.white,
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: ".46px",
        };
      case "small-table":
        return {
          fontWeight: 700,
          color: (theme) =>
            mode === "dark"
              ? theme.palette.primary.light
              : theme.palette.primary.main,
          background: "transparent",
          border: "1px solid",
          borderColor: "transparent",
          "&:hover": {
            background: (theme) => theme.palette.primary.gradient,
            color: "common.white",
          },
          px: 1,
          py: 0.2,
          fontSize: "13px",
          borderRadius: 1,
          ...(open
            ? {
                borderColor: (theme) => theme.palette.primary.main,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                bgcolor: (theme) => alpha(theme.palette.text.primary, 0.08),
              }
            : {}),
        };
      case "action-cell":
        return {
          color: "primary.light",
          whiteSpace: "nowrap",
          border: "1px solid",
          borderColor: "transparent",
          height: 30,
          ...(open
            ? {
                borderColor: (theme) => theme.palette.primary.main,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                bgcolor: (theme) => alpha(theme.palette.text.primary, 0.08),
              }
            : {}),
        };
      default:
        return {
          background: (theme) => theme.palette.primary.gradient,
          color: "common.white",
          fontWeight: 700,
          "&:hover": {
            background: (theme) => theme.palette.secondary.dark,
          },
          px: 2,
          py: 0.5,
          fontSize: 12,
          borderRadius: 1,
        };
    }
  };

  return (
    <>
      <Button
        id={anchorId}
        data-testid={type}
        aria-controls={menuId}
        aria-haspopup="menu"
        onClick={handleClick}
        variant={buttonVariant}
        size={buttonSize}
        fullWidth={buttonFullWidth}
        sx={buttonSx()}
        startIcon={buttonStartIcon}
        endIcon={buttonEndIcon}
      >
        Take Action
      </Button>
      <TakeActionMenu
        anchorId={anchorId}
        menuId={menuId}
        type={type}
        availableRemediations={availableRemediations}
        conversation={conversation}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      />
    </>
  );
}
